import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";

// Global KundaliKhoj CTA — a gold vertical banner flush to the LEFT edge,
// mid-height, on every screen size. The label reads bottom-to-top from the
// inside, so the thin strip never covers page content/text.
// On DESKTOP the close button dismisses it entirely (remembered). On MOBILE
// it is NEVER fully removed — closing minimises it to a small star tab the
// user can tap to expand again (so earlier dismissals still show the tab).
// Hidden on the KundaliKhoj journey itself.
const DISMISS_KEY = "kk_entry_dismissed";

export default function KundaliKhojEntry() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    try { return window.matchMedia("(max-width: 767px)").matches; } catch { return false; }
  });

  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia("(max-width: 767px)");
      const h = (e) => setIsMobile(e.matches);
      mq.addEventListener("change", h);
      return () => mq.removeEventListener("change", h);
    } catch { /* noop */ }
  }, []);

  if (pathname.startsWith("/kundali-khoj")) return null;
  // Desktop: a remembered dismissal removes it. Mobile: never fully removed.
  if (dismissed && !isMobile) return null;

  const isHindi = (i18n.language || "").toLowerCase().startsWith("hi");
  const label = isHindi ? "कुंडली" : "Kundali";

  const close = (e) => {
    e.stopPropagation();
    // On mobile, don't remove the banner — minimise it to a small tab the user
    // can tap to expand again. On desktop keep the full dismiss (remembered).
    if (isMobile) {
      setCollapsed(true);
      return;
    }
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_) {}
    setDismissed(true);
  };

  const expand = () => {
    setCollapsed(false);
    // Clear any legacy dismissal so the full banner comes back on mobile.
    if (dismissed) {
      setDismissed(false);
      try { localStorage.removeItem(DISMISS_KEY); } catch (_) {}
    }
  };

  // Show the small tab when the user collapsed it, OR when a mobile user had
  // dismissed it under the old behaviour (so it's still reachable).
  const showNub = collapsed || (isMobile && dismissed);

  if (showNub) {
    return (
      <button
        onClick={expand}
        data-testid="kundalikhoj-entry-expand"
        aria-label={label}
        className="fixed left-0 top-1/2 -translate-y-1/2 z-40 flex items-center justify-center h-11 w-7 rounded-r-lg bg-[#C9A227] text-[#3A2E0A] shadow-lg hover:bg-[#b08f1f] transition-colors"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    );
  }

  const onTap = () => {
    // Fire-and-forget tap tracking so the admin can see how many KundaliKhoj
    // visits the banner drives (backend counter + GA4 + PostHog).
    try { import("@/lib/api").then((m) => m.default.post("/kk/entry-click", { source: "global" }).catch(() => {})); } catch (_) {}
    try { if (window.gtag) window.gtag("event", "kundalikhoj_entry_click", { source: "global" }); } catch (_) {}
    try { if (window.posthog) window.posthog.capture("kundalikhoj_entry_click", { source: "global" }); } catch (_) {}
    navigate("/kundali-khoj?from=global");
  };

  return (
    <div className="fixed left-0 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center rounded-r-xl bg-[#C9A227] text-[#3A2E0A] shadow-lg">
      <button
        onClick={close}
        data-testid="kundalikhoj-entry-close"
        aria-label="Dismiss KundaliKhoj banner"
        className="w-full flex justify-center pt-1 pb-0.5 text-[#3A2E0A]/60 hover:text-[#3A2E0A] transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={onTap}
        data-testid="kundalikhoj-entry"
        aria-label={label}
        className="flex flex-col items-center gap-1.5 w-full px-1 pt-1.5 pb-2.5 rounded-br-xl hover:bg-[#b08f1f] transition-colors"
      >
        <span
          style={{ writingMode: "vertical-rl" }}
          className="text-[13px] font-extrabold tracking-wide rotate-180"
        >
          {label}
        </span>
      </button>
    </div>
  );
}
